// File location: components/client/form-components/FormSections.tsx

import React from "react";
import {
	Typography,
	TextField,
	FormControl,
	Autocomplete,
	Chip,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Box
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
	LinkedIn as LinkedInIcon,
	Person as PersonIcon,
	Email as EmailIcon,
	Work as WorkIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { FreelancerFrequency, CommunicationChannel } from "../../../models/Client";
import { BusinessDomain } from "../../../models/BusinessDomain";

interface BasicInfoSectionProps {
	firstName: string;
	lastName: string;
	email: string;
	jobTitle: string;
	errors: Record<string, string>;
	onChange: (field: string, value: string) => void;
}

export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
	firstName,
	lastName,
	email,
	jobTitle,
	errors,
	onChange
}) => {
	const { t } = useTranslation();

	return (
		<>
			<Grid size={12}>
				<Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
					{t("clientProfile.myInfo")}
				</Typography>
			</Grid>

			<Grid size={{ xs: 12, md: 4 }}>
				<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
					{t("clientProfile.firstName")}
				</Typography>
				<TextField
					fullWidth
					value={firstName}
					disabled
					InputProps={{
						startAdornment: <PersonIcon sx={{ mr: 1, color: "action.active" }} />
					}}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 4 }}>
				<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
					{t("clientProfile.lastName")}
				</Typography>
				<TextField
					fullWidth
					value={lastName}
					disabled
					InputProps={{
						startAdornment: <PersonIcon sx={{ mr: 1, color: "action.active" }} />
					}}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 4 }}>
				<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
					{t("clientProfile.email")}
				</Typography>
				<TextField
					fullWidth
					value={email}
					disabled
					InputProps={{
						startAdornment: <EmailIcon sx={{ mr: 1, color: "action.active" }} />
					}}
				/>
			</Grid>

			<Grid size={{ xs: 12, md: 6 }}>
				<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
					{t("clientProfile.jobTitle")} *
				</Typography>
				<TextField
					fullWidth
					required
					value={jobTitle}
					onChange={e => onChange("jobTitle", e.target.value)}
					error={!!errors.jobTitle}
					helperText={errors.jobTitle}
					InputProps={{
						startAdornment: <WorkIcon sx={{ mr: 1, color: "action.active" }} />
					}}
				/>
			</Grid>
		</>
	);
};

interface ExpertiseSectionProps {
	selectedExpertises: number[];
	selectedFrequency: FreelancerFrequency | "";
	selectedDomains: BusinessDomain[];
	selectedTools: number[];
	usesPlatforms?: boolean;
	showInsightTools: boolean;
	errors: Record<string, string>;
	expertises: any[];
	frequencies: FreelancerFrequency[];
	businessDomains: BusinessDomain[];
	tools: any[];
	onExpertisesChange: (value: number[]) => void;
	onFrequencyChange: (value: FreelancerFrequency) => void;
	onDomainsChange: (value: BusinessDomain[]) => void;
	onToolsChange: (value: number[]) => void;
	onUsesPlatformsChange: (value: boolean) => void;
	getExpertiseNameById: (id: number) => string;
	getToolNameById: (id: number) => string;
}

export const ExpertiseSection: React.FC<ExpertiseSectionProps> = ({
	selectedExpertises,
	selectedFrequency,
	selectedDomains,
	selectedTools,
	usesPlatforms,
	showInsightTools,
	errors,
	expertises,
	frequencies,
	businessDomains,
	tools,
	onExpertisesChange,
	onFrequencyChange,
	onDomainsChange,
	onToolsChange,
	onUsesPlatformsChange,
	getToolNameById
}) => {
	const { t } = useTranslation();

	return (
		<>
			<Grid size={12}>
				<Typography variant="h5" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
					{t("clientProfile.expertiseInfo")}
				</Typography>
			</Grid>

			{/* Expertises de Freelance Recherchés */}
			<Grid size={12}>
				<Box sx={{ mb: 3 }}>
					<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
						{t("clientProfile.freelancerExpertiseQuestion")} *
					</Typography>
					<FormControl fullWidth required error={!!errors.freelancerExpertises}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							id="freelancer-expertises"
							options={
								expertises?.sort((a, b) => {
									const typeComparison = a.type.localeCompare(b.type);
									if (typeComparison !== 0) return typeComparison;
									return a.id - b.id;
								}) || []
							}
							groupBy={option => option.type}
							getOptionLabel={option => t(`Expertises.${option.name}`)}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							value={
								selectedExpertises
									.map(id => expertises?.find(exp => exp.id === id))
									.filter(Boolean) || []
							}
							onChange={(_, newValue) => onExpertisesChange(newValue.map(exp => exp.id))}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => {
									const { key, ...tagProps } = getTagProps({ index });
									return (
										<Chip
											key={key}
											label={t(`Expertises.${option.name}`)}
											{...tagProps}
										/>
									);
								})
							}
							renderInput={params => (
								<TextField
									{...params}
									error={!!errors.freelancerExpertises}
									helperText={errors.freelancerExpertises}
									required
								/>
							)}
						/>
					</FormControl>
				</Box>
			</Grid>

			{/* Domaines d'activité */}
			<Grid size={12}>
				<Box sx={{ mb: 3 }}>
					<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
						{t("clientProfile.businessDomainQuestion")} *
					</Typography>
					<FormControl fullWidth required error={!!errors.businessDomains}>
						<Autocomplete
							multiple
							disableCloseOnSelect
							id="business-domains"
							options={businessDomains || []}
							getOptionLabel={option => t(`BusinessDomains.${option.label}`)}
							value={selectedDomains}
							onChange={(_, newValue) => onDomainsChange(newValue)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => {
									const { key, ...tagProps } = getTagProps({ index });
									return (
										<Chip
											key={key}
											label={t(`BusinessDomains.${option.label}`)}
											{...tagProps}
										/>
									);
								})
							}
							renderInput={params => (
								<TextField
									{...params}
									error={!!errors.businessDomains}
									helperText={errors.businessDomains}
									required
								/>
							)}
						/>
					</FormControl>
				</Box>
			</Grid>

			{/* Outils (profils Insight) */}
			{showInsightTools && (
				<Grid size={12}>
					<Box sx={{ mb: 3 }}>
						<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
							{t("clientProfile.insightToolsQuestion")}
						</Typography>
						<FormControl fullWidth>
							<Autocomplete
								multiple
								disableCloseOnSelect
								id="insight-tools"
								options={tools?.map(tool => tool.id) || []}
								getOptionLabel={option => getToolNameById(option)}
								value={selectedTools}
								onChange={(_, newValue) => onToolsChange(newValue)}
								renderTags={(value, getTagProps) =>
									value.map((option, index) => {
										const { key, ...tagProps } = getTagProps({ index });
										return (
											<Chip key={key} label={getToolNameById(option)} {...tagProps} />
										);
									})
								}
								renderInput={params => <TextField {...params} />}
							/>
						</FormControl>
					</Box>
				</Grid>
			)}

			{/* Recours aux Freelances */}
			<Grid size={12}>
				<Box sx={{ mb: 3 }}>
					<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
						{t("clientProfile.freelancerFrequencyQuestion")} *
					</Typography>
					<FormControl fullWidth required error={!!errors.freelancerFrequency}>
						<Autocomplete
							id="freelancer-frequency"
							options={frequencies || []}
							getOptionLabel={option => t(`enums.freelancerFrequency.${option}`)}
							value={selectedFrequency || null}
							onChange={(_, newValue) =>
								onFrequencyChange(newValue as FreelancerFrequency)
							}
							renderInput={params => (
								<TextField
									{...params}
									error={!!errors.freelancerFrequency}
									helperText={errors.freelancerFrequency}
									required
								/>
							)}
						/>
					</FormControl>
				</Box>
			</Grid>

			{/* Utilisation de Plateformes */}
			<Grid size={12}>
				<Typography variant="subtitle1" gutterBottom>
					{t("clientProfile.usesPlatformsQuestion")} *
				</Typography>
				<FormControl fullWidth required error={!!errors.usesPlatforms}>
					<RadioGroup
						row
						aria-labelledby="uses-platforms-radio-group"
						name="uses-platforms-radio"
						value={(() => {
							if (usesPlatforms === true) return "yes";
							if (usesPlatforms === false) return "no";
							return "";
						})()}
						onChange={e => onUsesPlatformsChange(e.target.value === "yes")}
					>
						<FormControlLabel
							value="yes"
							control={
								<Radio
									sx={{
										color: "secondary.main",
										"&.Mui-checked": { color: "secondary.main" }
									}}
								/>
							}
							label={t("common.yes")}
						/>
						<FormControlLabel
							value="no"
							control={
								<Radio
									sx={{
										color: "secondary.main",
										"&.Mui-checked": { color: "secondary.main" }
									}}
								/>
							}
							label={t("common.no")}
						/>
					</RadioGroup>
					{errors.usesPlatforms && (
						<FormHelperText error>{errors.usesPlatforms}</FormHelperText>
					)}
				</FormControl>
			</Grid>
		</>
	);
};

interface CommunicationSectionProps {
	communicationChannels: CommunicationChannel[];
	selectedCommunicationChannels: CommunicationChannel[];
	linkedInUrl: string;
	errors: Record<string, string>;
	onChannelsChange: (value: CommunicationChannel[]) => void;
	onLinkedInChange: (value: string) => void;
}

export const CommunicationSection: React.FC<CommunicationSectionProps> = ({
	communicationChannels,
	selectedCommunicationChannels,
	linkedInUrl,
	errors,
	onChannelsChange,
	onLinkedInChange
}) => {
	const { t } = useTranslation();

	// Check if LinkedIn is selected
	const isLinkedInSelected = selectedCommunicationChannels.includes(
		CommunicationChannel.LinkedInMessaging
	);

	return (
		<>
			<Grid size={12}>
				<Typography variant="h5" sx={{ mt: 2, mb: 2, fontWeight: 600 }}>
					{t("clientProfile.communicationPreferences")}
				</Typography>
			</Grid>

			<Grid size={12}>
				<Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
					{t("clientProfile.communicationChannelQuestion")} *
				</Typography>
				<FormControl fullWidth required error={!!errors.communicationChannels}>
					<Autocomplete
						multiple
						disableCloseOnSelect
						id="communication-channels"
						options={communicationChannels || []}
						getOptionLabel={option => t(`enums.communicationChannel.${option}`)}
						value={selectedCommunicationChannels}
						onChange={(_, newValue) => onChannelsChange(newValue)}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => {
								const { key, ...tagProps } = getTagProps({ index });
								return (
									<Chip
										key={key}
										label={t(`enums.communicationChannel.${option}`)}
										{...tagProps}
									/>
								);
							})
						}
						renderInput={params => (
							<TextField
								{...params}
								error={!!errors.communicationChannels}
								helperText={
									errors.communicationChannels || t("clientProfile.multipleAnswers")
								}
								required
							/>
						)}
					/>
				</FormControl>
			</Grid>

			{isLinkedInSelected && (
				<Grid size={{ xs: 12, md: 6 }}>
					<Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
						{t("clientProfile.linkedInUrl")} *
					</Typography>
					<TextField
						fullWidth
						required
						value={linkedInUrl}
						onChange={e => onLinkedInChange(e.target.value)}
						error={!!errors.linkedInUrl}
						helperText={errors.linkedInUrl || t("validation.linkedInHelp")}
						InputProps={{
							startAdornment: <LinkedInIcon sx={{ color: "action.active", mr: 1 }} />
						}}
					/>
				</Grid>
			)}
		</>
	);
};
